'use client'

import Head from 'next/head'
import Image from 'next/image'
import type { Shows, TVShow } from '@/lib/useShowsAPI'
import DataWrapper from '@/components/common/DataWrapper'
import useSWR from 'swr'
import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'

const fetcher = async (uri: URL | RequestInfo): Promise<TVShow[]> => {
  const response = await fetch(uri)
  if (!response.ok) {
    throw new Error('Fetch failed')
  }
  // assert the JSON shape directly
  const shows = (await response.json()) as Shows

  return shows.data
}

// use inline anonymous function to pass variables in query key
// https://react-query.tanstack.com/guides/query-keys#if-your-query-function-depends-on-a-variable-include-it-in-your-query-key
const useShowsAPI = (
  uri: URL | RequestInfo,
): UseQueryResult<TVShow[], Error> => {
  return useQuery<TVShow[], Error>({
    queryKey: ['showsAPI', uri],
    queryFn: () => fetcher(uri),
    staleTime: 1000 * 60 * 5,
  })
}

const TvShows = () => {
  const {
    data: dataSWR,
    error: errorSWR,
    isLoading: isLoadingSWR,
  } = useSWR<TVShow[], Error>('/api/shows', fetcher)

  const { status, data, error, isLoading, isFetching } =
    useShowsAPI('/api/shows')

  let displaySWR = undefined
  if (dataSWR) {
    displaySWR = dataSWR.map((s: TVShow) => {
      return (
        <li key={s.show.id}>
          {s.show.id}:{s.show.name} -
          <div className="w-40 h-auto">
            <Image
              src={s.show.image?.medium ?? 'https://picsum.photos/200'}
              alt={s.show.name}
              width={100}
              height={100}
              className="w-full h-auto"
            />
          </div>
        </li>
      )
    })
  }

  const text =
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.'

  return (
    <div>
      <Head>
        <title>Shows</title>
        <meta name="description" content="Shows" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <div>
        <h1 className="text-2xl font-bold">Shows</h1>
        <b>NEW</b>
        <DataWrapper text={text} isCode={false} />
        <b>react-query</b>
        {isLoading && <div>Loading TV shows...</div>}
        {isFetching && <div>Fetching TV shows...</div>}
        {status && <div>{status}</div>}
        {error instanceof Error && (
          <>
            <p>Error loading TV shows</p>
            <pre className="text-red-500">{JSON.stringify(error, null, 2)}</pre>
          </>
        )}
        {data && (
          <>
            <p>My favourite TV shows (react-query):</p>
            <DataWrapper text={JSON.stringify(data, null, 2)} isCode={true} />
          </>
        )}
        <b>swr</b>
        {isLoadingSWR && <div>Loading TV shows...</div>}
        {errorSWR && (
          <>
            <p>Error loading TV shows (swr):</p>
            <pre className="text-red-500">{JSON.stringify(error, null, 2)}</pre>
          </>
        )}
        {displaySWR && (
          <>
            <p>My favourite TV shows (swr):</p>
            <ul>{displaySWR}</ul>
          </>
        )}
        {dataSWR && (
          <>
            <p>My favourite TV shows (swr):</p>
            <DataWrapper
              text={JSON.stringify(dataSWR, null, 2)}
              isCode={true}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default TvShows
